import { useState } from 'react';
import { ChannelWithId, pauseChannel, resumeChannel, runCheckNow, useSessions } from '../lib/data';
import { ConfigForm } from './ConfigForm';
import { Badge } from '../components/Badge';
import { deriveChannelStatus } from '../lib/status';
import { formatTime } from '../lib/format';

/** Abbreviate a long Intercom id for table display; full id goes in the title tooltip. */
function shortId(id: string): string {
  return id.length > 6 ? `…${id.slice(-6)}` : id;
}

export function ChannelDetail({ channel, nowMs, onBack }: { channel: ChannelWithId; nowMs: number; onBack: () => void }) {
  const sessions = useSessions(channel.id);
  const [runMsg, setRunMsg] = useState<string | null>(null);
  const status = deriveChannelStatus(channel, nowMs);

  const doRunNow = async () => {
    setRunMsg('starting…');
    try {
      const res = await runCheckNow(channel.productId, channel.id);
      setRunMsg(res.launched ? 'check started ✓' : res.message ?? 'a check is already running');
    } catch {
      setRunMsg('run-now failed');
    }
  };

  const doPause = async () => {
    const hours = Number(prompt('Auto-resume after how many hours? (blank = indefinite)') ?? '');
    await pauseChannel(channel.productId, channel.id, hours > 0 ? nowMs + hours * 3_600_000 : null);
  };

  return (
    <div className="detail">
      <button className="link" onClick={onBack}>← back</button>
      <header className="detail-head">
        <h2>{channel.displayName}</h2>
        <Badge tone={status.tone}>{status.label}</Badge>
      </header>

      <div className="row">
        <button onClick={doRunNow}>Run check now</button>
        {channel.paused ? (
          <button onClick={() => resumeChannel(channel.productId, channel.id)}>Resume</button>
        ) : (
          <button onClick={doPause}>Pause</button>
        )}
        {runMsg && <span className="save-status">{runMsg}</span>}
      </div>

      <h3>Configuration</h3>
      <ConfigForm channel={channel} />

      <h3>Recent checks</h3>
      <table className="session-table">
        <thead>
          <tr><th>Started</th><th>Status</th><th>Reason</th><th>Contact</th><th>Cleanup</th></tr>
        </thead>
        <tbody>
          {sessions.length === 0 && <tr><td colSpan={5}>No checks yet.</td></tr>}
          {sessions.map((s) => (
            <tr key={s.runId} data-status={s.status}>
              <td>{formatTime(s.createdAt)}</td>
              <td>{s.status}</td>
              <td>{s.reason ?? '—'}</td>
              {/* Sending contact (R7/AE4). Nullable, and absent on pre-rotation docs. */}
              <td>{s.sentFromContactId ? <span title={s.sentFromContactId}>{shortId(s.sentFromContactId)}</span> : '—'}</td>
              <td>{s.cleanup}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
